/**
 * Quality Co-Pilot API service.
 *
 * The Co-Pilot reuses the Agent Console runtime and its SSE wire contract,
 * exposed through a profile-LOCKED door at `/api/quality-copilot` (the backend
 * forces the `recorder` profile). This service mirrors the agent-console SSE
 * client but points at the Co-Pilot endpoints, and adds the `voice_reply`
 * detection the voice UI keys off to render a spoken bubble.
 *
 * It deliberately reuses agentConsole.ts's pure parsing helpers + types rather
 * than duplicating the SSE plumbing.
 */
import api from './api';
import {
  CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  cancelConsoleTurn,
  renameConsoleConversation,
  streamConsoleSse,
  type CancelTurnResponse,
  type ConsoleEvent,
  type ConversationCreate,
  type ConversationDetail,
  type ConversationRead,
  type ModelsResponse,
  type ToolUseEvent,
} from './agentConsole';

export type {
  ConsoleEvent,
  ConversationCreate,
  ConversationDetail,
  ConversationRead,
  ModelOption,
  ModelsResponse,
  ToolUseEvent,
} from './agentConsole';

const BASE = '/quality-copilot';

/** Selectable models + this door's (Quality Co-Pilot) default, for the picker. */
export async function getModels(): Promise<ModelsResponse> {
  const { data } = await api.get<ModelsResponse>(`${BASE}/models`);
  return data;
}

export async function createConversation(
  body: ConversationCreate = {},
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(`${BASE}/conversations`, body, {
    timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  });
  return data;
}

/** A stored complaint photo reference returned by the attachments endpoint. */
export interface QualityCopilotAttachment {
  id: string;
  conversation_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  gs_uri: string;
}

/**
 * Upload one complaint photo to a conversation (image, ≤25 MiB) and return its
 * stored reference. The typed form names the uploaded photos in the submitted
 * message, so the recorder's signal records them.
 */
export async function uploadAttachment(
  conversationId: string,
  file: File,
): Promise<QualityCopilotAttachment> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<QualityCopilotAttachment>(
    `${BASE}/conversations/${conversationId}/attachments`,
    form,
    // The shared axios instance defaults Content-Type to application/json,
    // which clobbers FormData; the explicit multipart header makes axios emit
    // the real multipart body + boundary (repo convention — see brainDump.ts).
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/** An already-uploaded photo reference attached to a structured quality issue. */
export interface QualityIssueAttachmentRef {
  filename: string;
  gs_uri: string;
  mime_type?: string;
  size_bytes?: number;
}

/** The typed form's structured payload (mirrors the backend QualityIssueFormSubmit). */
export interface QualityIssueSubmitPayload {
  customer: string;
  category: string;
  severity: string;
  description: string;
  products: { item_code: string; quantity?: string }[];
  related_sage_so_no: string | null;
  lot_number: string | null;
  occurred_at: string | null;
  jobsite: string | null;
  business_impact: string | null;
  contact: string | null;
  attachments: QualityIssueAttachmentRef[];
  conversation_id: string | null;
}

/** The created quality_issues row (subset we render after a form submit). */
export interface QualityIssueRead {
  id: string;
  issue_no: string;
  status: string;
  category: string;
  severity: string;
  title: string;
}

/**
 * File a STRUCTURED quality issue from the typed form — writes a typed
 * `quality_issues` row (category / severity / product_skus[] / related_sage_so_no /
 * attachments[] …), preserving structure for eventual Sage mapping rather than
 * flattening to a free-text signal.
 */
export async function submitQualityIssueForm(
  payload: QualityIssueSubmitPayload,
): Promise<QualityIssueRead> {
  const { data } = await api.post<QualityIssueRead>(`${BASE}/quality-issues`, payload);
  return data;
}

export async function listConversations(
  opts: { limit?: number; offset?: number } = {},
): Promise<ConversationRead[]> {
  const params: Record<string, number> = {};
  if (opts.limit != null) params.limit = opts.limit;
  if (opts.offset != null) params.offset = opts.offset;
  const { data } = await api.get<{ items: ConversationRead[] }>(
    `${BASE}/conversations`,
    { params },
  );
  return data.items;
}

export async function getConversationDetail(
  conversationId: string,
): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(
    `${BASE}/conversations/${conversationId}`,
    { timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS },
  );
  return data;
}

/** Rename a Quality Co-Pilot conversation (editable title) via the shared path. */
export function renameConversation(conversationId: string, title: string) {
  return renameConsoleConversation(BASE, conversationId, title);
}

/**
 * Stop the conversation's in-flight turn (the composer's Stop button). Pass the
 * `runId` from the turn's `started` frame so a stale Stop can't abort a newer
 * turn. Idempotent — `{cancelled: false}` (the turn already finished) is a
 * normal outcome, not an error. The server emits the terminal `cancelled` +
 * `done` frames on the existing stream, which settle the UI.
 */
export function cancelMessage(
  conversationId: string,
  runId?: string | null,
): Promise<CancelTurnResponse> {
  return cancelConsoleTurn(BASE, conversationId, runId);
}

/**
 * True when a (bare or SDK-qualified) tool name is the spoken-summary tool.
 * Mirrors the backend `is_voice_reply_tool` so the UI can render a `voice_reply`
 * tool-use as a playable spoken bubble instead of a tool chip.
 */
export function isVoiceReplyTool(name: string): boolean {
  return name === 'voice_reply' || name.endsWith('__voice_reply');
}

/** Pull the spoken text out of a voice_reply tool-use input (`summary`). */
export function spokenSummaryFromToolUse(evt: ToolUseEvent): string {
  const summary = (evt.input as { summary?: unknown }).summary;
  return typeof summary === 'string' ? summary : '';
}

/**
 * Open the SSE stream for one Co-Pilot turn. Invokes `onEvent` for every parsed
 * frame; resolves when the server closes the stream (after `done`). Throws only
 * on a non-OK HTTP response — turn-level failures arrive as `error` frames. The
 * profile is forced server-side, so no `profile_id` is sent.
 */
export async function streamCopilotMessage(
  conversationId: string,
  prompt: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
  model?: string,
): Promise<void> {
  // Carry the selected model (last-turn-wins, persisted server-side); omitted ->
  // keep the conversation's current model. The profile stays forced server-side,
  // so no profile_id is sent. The shared loop handles the 401 refresh-and-retry.
  const payload: { prompt: string; model?: string } = { prompt };
  if (model) payload.model = model;
  return streamConsoleSse(
    `/api/quality-copilot/conversations/${conversationId}/message`,
    payload,
    onEvent,
    signal,
  );
}
